import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Client } from '@/types';

const STORAGE_KEY = 'scheduler-customer-selector-state';
const MAX_RECENT_CUSTOMERS = 10;

interface CustomerSelectorState {
  favourites: string[];
  recentlyUsed: Array<{ id: string; timestamp: number }>;
}

export function useCustomerSelector(customers: Client[]) {
  const [state, setState] = useState<CustomerSelectorState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          favourites: parsed.favourites || [],
          recentlyUsed: parsed.recentlyUsed || [],
        };
      }
    } catch (error) {
      console.error('Failed to load customer selector state:', error);
    }
    return { favourites: [], recentlyUsed: [] };
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save customer selector state:', error);
    }
  }, [state]);

  const toggleFavourite = useCallback((customerId: string) => {
    setState((prev) => {
      const isFav = prev.favourites.includes(customerId);
      return {
        ...prev,
        favourites: isFav
          ? prev.favourites.filter((id) => String(id) !== String(customerId))
          : [...prev.favourites, customerId],
      };
    });
  }, []);

  const recordCustomerAccess = useCallback((customerId: string) => {
    setState((prev) => {
      const filtered = prev.recentlyUsed.filter((item) => String(item.id) !== String(customerId));
      const updated = [
        { id: customerId, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_RECENT_CUSTOMERS);
      return {
        ...prev,
        recentlyUsed: updated,
      };
    });
  }, []);

  const enrichedCustomers = useMemo(() => {
    return customers.map((customer) => ({
      ...customer,
      isFavourite: state.favourites.includes(String(customer.id)),
      lastAccessedDate: state.recentlyUsed.find((item) => String(item.id) === String(customer.id))
        ? new Date(state.recentlyUsed.find((item) => String(item.id) === String(customer.id))!.timestamp)
        : undefined,
    }));
  }, [customers, state.favourites, state.recentlyUsed]);

  const favouriteCustomers = useMemo(() => {
    return enrichedCustomers
      .filter((c) => c.isFavourite)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [enrichedCustomers]);

  const recentCustomers = useMemo(() => {
    const recentIds = state.recentlyUsed.map((item) => item.id);

    const accessedCustomers = enrichedCustomers
      .filter((c) => recentIds.includes(String(c.id)))
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      });

    if (accessedCustomers.length < 5) {
      const accessedIds = new Set(accessedCustomers.map(c => String(c.id)));
      const otherCustomers = enrichedCustomers
        .filter(c => !accessedIds.has(String(c.id)))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      return [...accessedCustomers, ...otherCustomers].slice(0, 5);
    }

    return accessedCustomers.slice(0, 5);
  }, [enrichedCustomers, state.recentlyUsed]);

  return {
    enrichedCustomers,
    favouriteCustomers,
    recentCustomers,
    toggleFavourite,
    recordCustomerAccess,
    isFavourite: (customerId: string) => state.favourites.includes(customerId),
  };
}
